import { useEffect, useState } from "react";
import NewOrganizationFormView from "./NewOrganizationFormView/NewOrganizationFormView";
import useOrganizations from "../../../hooks/useOrganizations";
import inputValidityChecker from "../../../../../utils/inputValidityChecker";
import {
  OrganizationPatch,
} from "../../../types/organization.types";
import { useNavigate } from "react-router-dom";
import useSingleOrganization from "../../../hooks/useSingleOrganization";
import { WalletCurrency } from "../../../../wallet/types/wallet.types";


export interface OrganizationFormData {
  name?: string;
  description?: string;
  country?: string;
  city?: string;
  street?: string;
  zip_code?: string;
  currency?: WalletCurrency;
}

export interface OrganizationFormErrorStatus {
  isErrorName: boolean;
  isErrorDescription: boolean;
  isErrorCountry: boolean;
  isErrorCity: boolean;
  isErrorStreet: boolean;
  isErrorZipCode: boolean;
  errorMessage?: string | null;
  allGood: boolean;
  isCurencyError?: boolean;
}

const INITIAL_FORM_DATA: OrganizationFormData = {
  name: "",
  description: "",
  country: "",
  city: "",
  street: "",
  zip_code: "",
};

const INITIAL_ERROR_STATUS: OrganizationFormErrorStatus = {
  isErrorName: false,
  isErrorDescription: false,
  isErrorCountry: false,
  isErrorCity: false,
  isErrorStreet: false,
  isErrorZipCode: false,
  errorMessage: null,
  isCurencyError: false,
  allGood: true,
};

export interface NewOrganizationFormPresenterProps{
    onSuccessfulSubmit?: () => void;
    isUpdate?: boolean;
    organizationIdToUpdate?: string;
}

/**
 * NewOrganizationFormPresenter
 * @param param0 
 * @returns 
 */
export default function NewOrganizationFormPresenter({onSuccessfulSubmit, isUpdate, organizationIdToUpdate}: NewOrganizationFormPresenterProps) {
  const [formData, setFormData] = useState<OrganizationFormData>({
    ...INITIAL_FORM_DATA,
  });
  const [errorStatus, setErrorStatus] = useState<OrganizationFormErrorStatus>({
    ...INITIAL_ERROR_STATUS,
  });
  const [errorMessages, setErrorMessages] = useState<string | null>(null);

  const { createNewOrgnizationQuery } = useOrganizations();

  const {singleOrganizationQuery, patchOrganizationQuery} = useSingleOrganization(organizationIdToUpdate);

  useEffect(() => {
    if (isUpdate && singleOrganizationQuery.data) {
      const organizationData = singleOrganizationQuery.data;
      setFormData({
        name: organizationData.organization_name,
        description: organizationData.organization_description,
        country: organizationData.organization_country,
        city: organizationData.organization_city,
        street: organizationData.organization_street,
        zip_code: organizationData.organization_zip_code,
        currency: organizationData.organization_currency,
      });
    }

  },[])// eslint-disable-line
  


const navigate = useNavigate();
  const handleSubmit = () => {
    const errorStatus = checkOrganizationFormDataValidity(formData);
    setErrorStatus(errorStatus);
    setErrorMessages(errorStatus.errorMessage || null);
    
    if (errorStatus.allGood) {
      const organizationData = convertFormDataToOrganization(formData);
      if (isUpdate && singleOrganizationQuery.data) {
        patchOrganizationQuery.mutate({
          data: organizationData,
          callback: ({result, error}) => {
              if (error) {
                  setErrorMessages(error.message);
              } 
              if (result) {
  
                  onSuccessfulSubmit && onSuccessfulSubmit();

              }
          },
        });
      }else{

      

      createNewOrgnizationQuery.mutate({
        newData: organizationData,
        callback: ({result, error}) => {
            if (error) {
                setErrorMessages(error.message);
            } 
            if (result) {
                setFormData({...INITIAL_FORM_DATA});
                onSuccessfulSubmit && onSuccessfulSubmit();
                navigate(result.organization_id);
            }
        },
      });
    }
    }
  };

  if (isUpdate && singleOrganizationQuery.isLoading) {
    return <div>Chargement...</div>;
  }
  return (
    <NewOrganizationFormView
      organizationFormData={formData}
      setOrganizationFormData={setFormData}
      errorStatus={errorStatus}
      messageError={errorMessages}
      handleSubmit={handleSubmit}
      isLoading={createNewOrgnizationQuery.isLoading}
      submitLabel={isUpdate ? "Modifier" : "Créer"}
      description={!isUpdate ? "Saisissez les informations de la nouvelle organisation" : "Modifier les informations de l'organisation"}
    />
  );
}

const checkOrganizationFormDataValidity = (
  formData: OrganizationFormData
): OrganizationFormErrorStatus => {
  const errorStatus: OrganizationFormErrorStatus = { ...INITIAL_ERROR_STATUS };

  const errorMessageList: string[] = [];
  if (
    !formData.name ||
    !inputValidityChecker.validateNotEmptyText(formData.name)
  ) {
    errorStatus.isErrorName = true;
    errorMessageList.push("Le nom de l'organisation est obligatoire");
    errorStatus.allGood = false;
  }

  if (!inputValidityChecker.validateNotEmptyText(formData.currency)) {
      errorStatus.isCurencyError = true;
      errorMessageList.push("La devise de l'organisation est obligatoire");
      errorStatus.allGood = false;
    }
  

  errorStatus.errorMessage =
    (errorMessageList.length > 0 && errorMessageList.join(", ")) || null;

  return errorStatus;
};

const convertFormDataToOrganization = (
  formData: OrganizationFormData
): OrganizationPatch => {
  return {
    organization_name: formData.name,
    organization_description: formData.description,
    organization_country: formData.country,
    organization_city: formData.city,
    organization_street: formData.street,
    organization_zip_code: formData.zip_code,
    organization_currency: formData.currency,
  };
};
